import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { DialogFullScreen, useDialog, Carousel, useCarousel, P } from '@yuhu-inc/yuhui';
import { Loading } from 'components/ui/LoadingIndicator';
import { createDocumentTemplate } from 'actions/landlordDocumentTemplatesActions';

import DocumentTemplateSetupPage from './carousel_slides/DocumentTemplateSetupPage';
import { DocumentTemplateConfigureSignaturesPage } from './carousel_slides/DocumentTemplateConfigureSignaturesPage';
import { DocumentTemplateRolesPage } from './carousel_slides/DocumentTemplateRolesPage';
import { DocumentTemplateUploadFilePage } from './carousel_slides/DocumentTemplateUploadFilePage';
import { DocumentTemplateAdditionalFieldsPage } from './carousel_slides/DocumentTemplateAdditionalFieldsPage';
import { DocumentTemplateAdditionalChargesPage } from './carousel_slides/DocumentTemplateAdditionalChargesPage';
import { DocumentTemplateServiceUtilitiesPage } from './carousel_slides/DocumentTemplateServiceUtilitiesPage';
import { DocumentTemplateReviewServiceUtilitiesPage } from './carousel_slides/DocumentTemplateReviewServiceUtilitiesPage';
import DocumentTemplateWizardError from './DocumentTemplateWizardError';

import DocumentTemplateErrorIcon from 'assets/sloth_invitation_expired.svg';
import BackArrowIcon from '@material-ui/icons/ArrowBackIosOutlined';

import { getTranslatedString } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';

import { colors } from 'helpers/ColorHelper';
import { DialogForConfirmation } from 'components/shared/DialogForConfirmation';

const StyledBackButton = styled.button`
  position: absolute;
  top: 62px;
  left: 62px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${colors.ULTRAMARINE_BLUE};
  cursor: pointer;
  background: none;
  border: none;
  ${media.mobile`
  top: 28px;
  left: 28px;
  font-size: 12px;
  `};
`;

const StyledBackText = styled.span`
  margin-top: 8px;
  color: ${colors.GRAY};
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const StyledHeader = styled(P)`
  font-size: 24px;
  font-family: 'Circular-Black', sans-serif;
  text-align: center;
`;

const StyledP = styled(P)`
  font-size: 18px;
  text-align: center;
`;

const StyledIcon = styled.img`
  width: 180px;
  height: 180px;
  margin-top: 15px;
  ${media.mobile`
    width: 130px;
    height: 130px;
  `};
`;

export const CreateDocumentTemplateDialog = ({ handleCreateNewDocumentTemplate, children }) => {
  const dialog = useDialog();
  const confirmationDialog = useDialog();

  const handleEscButtonClick = () => {
    confirmationDialog.show();
    return false;
  };

  const handleConfirmation = (confirmed) => {
    if (confirmed) {
      dialog.hide();
      confirmationDialog.hide();
    } else {
      confirmationDialog.hide();
    }
  };

  return (
    <div>
      <DialogFullScreen {...dialog} onEscButtonClick={handleEscButtonClick}>
        <DocumentTemplateCreator
          closeDialog={dialog.hide}
          handleCreateNewDocumentTemplate={handleCreateNewDocumentTemplate}
        />
      </DialogFullScreen>
      <DialogForConfirmation
        handleConfirmation={handleConfirmation}
        noKey="landlordPortal.documentTemplate.carousel.cancel"
        yesKey="landlordPortal.documentTemplate.carousel.close"
        {...confirmationDialog}
      >
        <StyledContainer>
          <StyledHeader contentKey="landlordPortal.documentTemplate.deleteConfirmationHeader" />
          <StyledP contentKey="landlordPortal.documentTemplate.deleteConfirmationP" />
        </StyledContainer>
      </DialogForConfirmation>
      {children(dialog)}
    </div>
  );
};

export const DOCUMENT_TEMPLATE_SETUP_PAGE = 0;
export const DOCUMENT_TEMPLATE_ROLES_PAGE = 1;
export const DOCUMENT_TEMPLATE_UPLOAD_FILE_PAGE = 2;
export const DOCUMENT_TEMPLATE_ADDITIONAL_FIELDS_PAGE = 3;
export const DOCUMENT_TEMPLATE_ADDITIONAL_CHARGES_PAGE = 4;
export const DOCUMENT_TEMPLATE_SERVICE_UTILITIES_PAGE = 5;
export const DOCUMENT_TEMPLATE_REVIEW_SERVICE_UTILITIES_PAGE = 6;
export const DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE = 7;

export const LEASE = 'lease';
export const FIXED_ADDENDUM = 'fixed_addendum';
export const CONDITIONAL_ADDENDUM = 'conditional_addendum';

const StyledCarousel = styled(Carousel)`
  visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
`;

const DocumentTemplateCreator = connect(
  (store) => ({
    upsertingDocumentTemplate: store.landlordDocumentTemplates.isUpsertingDocumentTemplate,
    documentTemplate: store.landlordDocumentTemplates.documentTemplate,
    clientId: store.landlordSignableDoc.clientId,
  }),
  {
    createDocumentTemplate,
  }
)(
  ({
    createDocumentTemplate,
    upsertingDocumentTemplate,
    handleCreateNewDocumentTemplate,
    documentTemplate,
    closeDialog,
    clientId,
  }) => {
    const [template, setTemplate] = useState({});
    const [showErrorPage, setShowErrorPage] = useState(false);

    useEffect(() => {
      window.onbeforeunload = function () {
        return false;
      };

      return () => {
        window.onbeforeunload = function () {
          return undefined;
        };
      };
    });

    const { pageIndex, goTo, back, forward, ...rest } = useCarousel({
      numOfPages: 8,
    });

    const handleCreateTemplate = useCallback(
      async (formValue) => {
        // wait until the document template is created before re-fetching the data
        try {
          await createDocumentTemplate({ ...template, ...formValue });
          setShowErrorPage(false);
          return handleCreateNewDocumentTemplate();
        } catch (err) {
          setShowErrorPage(true);
        }
      },
      [createDocumentTemplate, template, handleCreateNewDocumentTemplate]
    );

    const handleClickBack = (e) => {
      e.preventDefault();
      if (showErrorPage) {
        setShowErrorPage(false);
      }
      if (template?.services_and_utilities?.length === 0) {
        // If there is no additional service and utilities, skip review page and go to service and utilities page
        // If this needs to change, check the handle submit on DocumentTemplateServiceUtilitesPage.js as well
        goTo(DOCUMENT_TEMPLATE_SERVICE_UTILITIES_PAGE);
      } else {
        back();
      }
    };

    const pages = [
      <DocumentTemplateSetupPage forward={forward} setTemplate={setTemplate} />,
      <DocumentTemplateRolesPage
        forward={forward}
        setTemplate={setTemplate}
        isLease={template.signable_doc_type === LEASE}
        isConditionalAddendum={template.signable_doc_type === CONDITIONAL_ADDENDUM}
      />,
      <DocumentTemplateUploadFilePage forward={forward} setTemplate={setTemplate} />,
      <DocumentTemplateAdditionalFieldsPage forward={forward} setTemplate={setTemplate} />,
      <DocumentTemplateAdditionalChargesPage
        forward={forward}
        setTemplate={setTemplate}
        goTo={goTo}
        handleCreateTemplate={handleCreateTemplate}
        template={template}
      />,
      <DocumentTemplateServiceUtilitiesPage
        forward={forward}
        setTemplate={setTemplate}
        goTo={goTo}
        handleCreateTemplate={handleCreateTemplate}
      />,
      <DocumentTemplateReviewServiceUtilitiesPage
        forward={forward}
        servicesAndUtilities={template?.services_and_utilities}
        handleCreateTemplate={handleCreateTemplate}
      />,
      <DocumentTemplateConfigureSignaturesPage
        closeDialog={closeDialog}
        documentTemplate={documentTemplate}
        clientId={clientId}
      />,
    ];

    return (
      <React.Fragment>
        <div>
          <Loading shouldRender={upsertingDocumentTemplate} isFullscreen={true} />
          {!upsertingDocumentTemplate && showErrorPage && (
            <DocumentTemplateWizardError
              headerText={getTranslatedString('landlordPortal.documentTemplate.wizard.errorHeader')}
              bodyText={getTranslatedString('landlordPortal.documentTemplate.wizard.errorBody')}
              onClick={() => handleCreateTemplate()}
              buttonText={getTranslatedString('landlordPortal.documentTemplate.wizard.errorCta')}
              background={colors.PALE_GREY}
              iconSource={<StyledIcon src={DocumentTemplateErrorIcon} />}
            />
          )}
          <StyledCarousel
            pageIndex={pageIndex}
            {...rest}
            pages={pages}
            hidden={upsertingDocumentTemplate || showErrorPage}
          />
          {(showErrorPage ||
            (!upsertingDocumentTemplate &&
              pageIndex !== DOCUMENT_TEMPLATE_SETUP_PAGE &&
              pageIndex !== DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE)) && (
            <StyledBackButton onClick={handleClickBack}>
              <BackArrowIcon />
              <StyledBackText>
                {getTranslatedString('landlordPortal.documentTemplate.carousel.back')}
              </StyledBackText>
            </StyledBackButton>
          )}
        </div>
      </React.Fragment>
    );
  }
);
